/**
 * ServiceActivityBreakdownPanel — S2: Participant Service Segments (Final PRD §12–18, §49)
 * ─────────────────────────────────────────────────────────────────────────────
 * The shift drawer's segment editor. A segment is a timed part of ONE participant shift linked to an
 * Agreement Line + Participant Service Item. Rules honoured here:
 *  - The service selector offers ONLY the participant's ACTIVE agreement lines/PSIs for the shift date
 *    (/shifts/segments/authorised-services) — never the full catalogue.
 *  - Reads/writes ONLY /api/shifts/{id}/service-allocations (the segment row).
 *  - Locked shifts (clocked / delivered / approved / billed / invoiced) refuse edits → correction path.
 *  - Billing disposition (BILLABLE / NON_BILLABLE) is shown as a placeholder until S5.
 *  - The legacy `multi_service_shift_allocations_enabled` flag only gates catalogue-based legacy rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { addItem, validateItems } from './extraServiceItems';   // client#2: several line items in one New segment
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TrinitiTimePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Pencil, Lock, Info, X, Activity, Car, Star, DollarSign, AlertTriangle, ChevronDown, ChevronRight, Scissors } from 'lucide-react';
import { RATIOS, shiftHours, emptyForm, authorisedOptions, segmentErrorMessage, lockMessage, BILLING_FINAL_LOCKS } from './segmentRules';
import { permissionReason } from './shiftSheetRules';
import { canRosterWithoutAgreement as rolesWithoutAgreement } from './createShiftServiceRules';
import { useShiftSheetDraft } from './ShiftSheetDraftContext';
// UC-A-6: SegmentBillingPanel (S5) is no longer mounted on the roster — component and /billing/segments API kept for UC-C.

const EDITOR_ID = 'segment-editor';

const fmtHours = (h) => (h == null ? '—' : `${Number(h).toFixed(2)}h`);

// Build a human-readable label for a funding plan dropdown row.
// Falls back to a short ID suffix only if every meaningful field is missing.
const fundingPlanLabel = (p) => {
  if (!p) return '—';
  const name = p.plan_name || p.name || p.plan_number;
  if (name) {
    // Add date range if available for plans with the same name
    if (p.start_date && p.end_date) {
      return `${name} (${String(p.start_date).slice(0, 10)} → ${String(p.end_date).slice(0, 10)})`;
    }
    return name;
  }
  // Last-resort fallback — show "Plan #<last 6 chars>" rather than the full ObjectId
  if (p.id) return `Plan #${String(p.id).slice(-6)}`;
  return '—';
};

export function ServiceActivityBreakdownPanel({ shift, onChange, onMeta, compact = false, readOnly = false }) {
  const { hasPermission, user } = useAuth();
  const draft = useShiftSheetDraft();
  const canView = hasPermission('service_allocations.view');
  const canCreate = hasPermission('service_allocations.create');
  const canUpdate = hasPermission('service_allocations.update');
  const canDelete = hasPermission('service_allocations.delete');
  const canCorrect = hasPermission('roster.manage');   // S4-6 controlled correction path
  // Enterprise Phase 1.5 — admin-only operational summary tile
  const canViewOperationalTile = hasPermission('allocation.view');
  // Enterprise Phase 2A — admin-only preview tile (financial projection, read-only)
  const canViewPreviewTile = hasPermission('allocation.preview');

  const [loading, setLoading] = useState(false);
  const [flagOn, setFlagOn] = useState(false);          // legacy catalogue-allocation flag (informational)
  const [locked, setLocked] = useState(null);          // S2 §18: lock reason from the server, null when editable
  const [correctionPath, setCorrectionPath] = useState('');
  const [rows, setRows] = useState([]);
  const [authorised, setAuthorised] = useState(null);  // S2-2 selector payload: { agreement, agreement_status, reason, lines[] }
  const [fundingPlans, setFundingPlans] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | <segment_id>
  const [form, setForm] = useState(emptyForm(shift));
  const [correctionReason, setCorrectionReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [splitting, setSplitting] = useState(null); // UC-A Plan "split": { row, time }
  // Enterprise Phase 1.5 — operational summary state (read-only)
  const [opsSummary, setOpsSummary] = useState(null); // null | { feature_enabled, summary }
  // Enterprise Phase 2A — financial preview state (read-only projection)
  const [preview, setPreview] = useState(null); // null | PreviewDoc
  // Enterprise Phase 2A.5 — preview lines drawer expand/collapse (admin-only)
  const [previewLinesOpen, setPreviewLinesOpen] = useState(false);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!shift?.id || !canView) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [a, ff] = await Promise.all([
          api.get(`/shifts/${shift.id}/service-allocations`),
          api.get('/shifts/service-allocations/feature-flag'),
        ]);
        if (!active) return;
        setRows((a.data?.data || []).filter(r => r.status !== 'DELETED'));
        setLocked(a.data?.locked || null);
        setCorrectionPath(a.data?.correction_path || '');
        setFlagOn(!!ff.data?.data?.enabled);
      } catch (e) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canView]);

  // Enterprise Phase 1.5 — fetch operational summary (admin-only, additive).
  useEffect(() => {
    if (!shift?.id || !canViewOperationalTile) {
      setOpsSummary(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/admin/allocations/operational-summary/${shift.id}`);
        if (active) setOpsSummary(r.data?.data || null);
      } catch {
        if (active) setOpsSummary(null);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canViewOperationalTile]);

  // Enterprise Phase 2A — fetch financial preview (admin-only, additive).
  useEffect(() => {
    if (!shift?.id || !canViewPreviewTile) {
      setPreview(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/admin/allocations/preview/${shift.id}`);
        if (active) setPreview(r.data?.data || null);
      } catch {
        if (active) setPreview(null);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canViewPreviewTile]);

  // S2-2 (PRD §14, §49): the selector is the participant's ACTIVE agreement → lines → PSIs for the shift date.
  // The full service catalogue is deliberately NOT loaded — an arbitrary catalogue item can never be rostered.
  useEffect(() => {
    if (!shift?.participant_id || !canView) return;
    let active = true;
    (async () => {
      try {
        const [auth, fp] = await Promise.all([
          api.get(`/shifts/segments/authorised-services?participant_id=${shift.participant_id}&service_date=${shift.date}`),
          api.get(`/funding/plans/by-participant/${shift.participant_id}`).catch(() => ({ data: { data: [] } })),
        ]);
        if (!active) return;
        setAuthorised(auth.data?.data || null);
        setFundingPlans(fp.data?.data || fp.data?.items || []);
      } catch (e) {
        if (active) setAuthorised(null);
      }
    })();
    return () => { active = false; };
  }, [shift?.participant_id, shift?.date, canView]);

  const fundingPlanLookup = useMemo(() => {
    const map = new Map();
    (fundingPlans || []).forEach(p => { if (p?.id) map.set(p.id, p); });
    return map;
  }, [fundingPlans]);

  const psiOptions = useMemo(() => authorisedOptions(authorised), [authorised]);
  // Client requirement (13 Oct 2026): Admin / Super Admin (and, per UAT, Coordinator) can add segments without an agreement line — a catalogue line
  // item (house/participant branch catalogue for SIL shifts, service catalogue for 1:1) billed from the funding plan —
  // when the shift was rostered without an agreement or the participant has no usable agreement line on the date.
  const canRosterWithoutAgreement = rolesWithoutAgreement(user);
  const usableLines = psiOptions.some(o => !o.disabled);
  const noAgreementSegments = canRosterWithoutAgreement && !!shift?.participant_id && (shift?.billing_source === 'FUNDING_PLAN' || (authorised !== null && !usableLines));
  const useBranchCatalogue = !!(shift?.house_id || shift?.linked_house_id || shift?.branch_catalogue_item_id);
  const [catalogueOptions, setCatalogueOptions] = useState([]);   // [{ id, label, kind: 'branch' | 'service' }]
  useEffect(() => {
    if (!noAgreementSegments || !shift?.date) { setCatalogueOptions([]); return undefined; }
    let active = true;
    (async () => {
      try {
        if (useBranchCatalogue) {
          const q = new URLSearchParams({ house_id: shift.house_id || shift.linked_house_id, shift_date: String(shift.date).slice(0, 10), resident_ids: shift.participant_id });
          const r = await api.get(`/roster/no-agreement-sil-context?${q}`);
          if (active) setCatalogueOptions((r.data?.data?.options || []).map(o => ({ id: o.id, kind: 'branch',
            label: `${o.name}${o.support_item_number ? ` · ${o.support_item_number}` : ''}${o.rate ? ` · $${Number(o.rate).toFixed(2)}/${o.unit || 'unit'}` : ''}` })));
        } else {
          const r = await api.get('/services/catalog', { params: { mode: 'selection' } });
          const list = r.data?.data || r.data?.items || [];
          if (active) setCatalogueOptions(list.map(o => ({ id: o.id, kind: 'service',
            label: `${o.name}${o.ndis_support_item_number || o.ndis_code ? ` · ${o.ndis_support_item_number || o.ndis_code}` : ''}` })));
        }
      } catch { if (active) setCatalogueOptions([]); }
    })();
    return () => { active = false; };
  }, [noAgreementSegments, useBranchCatalogue, shift?.id, shift?.date, shift?.participant_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const catalogueLabel = (row) => (catalogueOptions.find(o => o.id === (row.branch_catalogue_item_id || row.service_catalog_id)) || {}).label;
  const lineLookup = useMemo(() => {
    const map = new Map();
    (authorised?.lines || []).forEach(ln => map.set(ln.agreement_line_id, ln));
    return map;
  }, [authorised]);

  const serviceLabel = (row) => {
    const ln = row.agreement_line_id ? lineLookup.get(row.agreement_line_id) : null;
    if (ln) return ln.support_item_number ? `${ln.service_name} (${ln.support_item_number})` : ln.service_name;
    if (row.segment_kind === 'NO_AGREEMENT_SEGMENT' && catalogueLabel(row)) return catalogueLabel(row);
    if (row.support_item_number) return row.support_item_number;
    return row.service_catalog_id ? `Service #${String(row.service_catalog_id).slice(-6)}` : '—';
  };

  const shiftDuration = useMemo(() => shiftHours(shift), [shift]);
  const myRows = rows.filter(r => !shift?.participant_id || String(r.participant_id) === String(shift.participant_id));
  const allocated = myRows.reduce((acc, r) => acc + (Number(r.duration_hours) || 0), 0);
  const remaining = Math.max(0, shiftDuration - allocated);
  const overAllocated = allocated > shiftDuration + 0.05;
  const canEdit = !locked && (!!authorised?.agreement || noAgreementSegments) && !readOnly;
  // Delivered shift (e.g. the worker clocked in before a segment was planned): a rostering manager can still add or
  // correct a segment, with a reason that is kept in the shift history (S4-6 controlled correction).
  const canAddWithReason = !!locked && canCorrect && (!!authorised?.agreement || noAgreementSegments) && !readOnly;

  // UC-A: lift lock / authority / rows to the sheet header, timeline and alert line (no extra API call).
  useEffect(() => { onMeta && onMeta({ locked, correctionPath, authorised, rows }); }, [onMeta, locked, correctionPath, authorised, rows]);

  // ── handlers ───────────────────────────────────────────────────────────────
  const reload = async () => {
    if (!shift?.id) return;
    try {
      const a = await api.get(`/shifts/${shift.id}/service-allocations`);
      setRows((a.data?.data || []).filter(r => r.status !== 'DELETED'));
      setLocked(a.data?.locked || null);
      setCorrectionPath(a.data?.correction_path || '');
      onChange && onChange();
    } catch { /* ignore */ }
  };

  const openNew = () => {
    setCorrectionReason('');
    const catalogueMode = noAgreementSegments && (!usableLines || shift?.billing_source === 'FUNDING_PLAN');
    setForm({ ...emptyForm(shift), participant_service_item_id: catalogueMode ? '' : (psiOptions.find(o => !o.disabled)?.value || ''), is_primary: myRows.length === 0,
      _mode: catalogueMode ? 'catalogue' : 'psi', line_item: catalogueMode ? (shift?.branch_catalogue_item_id || shift?.service_catalog_id || '') : '',
      funding_plan_id: shift?.funding_plan_id || '', _extra: [] });
    setEditing('new');
  };

  const openEdit = (row) => {
    setForm({
      participant_service_item_id: row.participant_service_item_id || '',
      allocation_start_time: row.allocation_start_time || '',
      allocation_end_time: row.allocation_end_time || '',
      operational_ratio: row.operational_ratio || shift?.support_ratio || '1:1',
      is_primary: !!row.is_primary,
      notes: row.notes || '',
      _version: row.version,
      _mode: row.segment_kind === 'NO_AGREEMENT_SEGMENT' ? 'catalogue' : 'psi',
      line_item: row.branch_catalogue_item_id || row.service_catalog_id || '',
      funding_plan_id: row.funding_plan_id || '',
      _extra: [],
    });
    setEditing(row.id);
  };

  const closeEditor = () => { setEditing(null); setInlineError(''); };

  // No-agreement segment: the catalogue line item (branch row or service catalogue id) + funding plan instead of a PSI.
  const lineItemFields = (lineItem, fundingPlanId) => {
    const kind = (catalogueOptions.find(o => o.id === lineItem) || {}).kind || (useBranchCatalogue ? 'branch' : 'service');
    return { ...(kind === 'branch' ? { branch_catalogue_item_id: lineItem } : { service_catalog_id: lineItem }), funding_plan_id: fundingPlanId || undefined };
  };

  // client#2: a New segment can carry extra line items, each with its own window — added in one save.
  const mainItemValue = form._mode === 'catalogue' ? form.line_item : form.participant_service_item_id;
  const extraItemOptions = form._mode === 'catalogue'
    ? catalogueOptions.map(o => ({ value: o.id, label: o.label, keywords: o.code || o.support_item_number || '' }))
    : psiOptions;
  const newItems = () => [{ _key: 'main', psi: mainItemValue, start: form.allocation_start_time, end: form.allocation_end_time }, ...(form._extra || [])];
  const addExtraItem = () => {
    const next = addItem(shift, newItems());   // splits the last row in half, like Split
    setForm(f => ({ ...f, allocation_end_time: next[0].end, _extra: next.slice(1) }));
  };
  const updateExtraItem = (key, patch) => setForm(f => ({ ...f, _extra: (f._extra || []).map(x => (x._key === key ? { ...x, ...patch } : x)) }));
  const removeExtraItem = (key) => setForm(f => ({ ...f, _extra: (f._extra || []).filter(x => x._key !== key) }));
  const itemFields = (value) => (form._mode === 'catalogue' ? lineItemFields(value, form.funding_plan_id) : { participant_service_item_id: value });

  // New segment with extra line items: checked up front, then saved in order; a refused item keeps the unsaved ones open.
  const saveNewItems = async () => {
    const items = newItems();
    const problem = validateItems(shift, items);
    if (problem) throw { detail: { message: problem } };   // eslint-disable-line no-throw-literal
    let saved = 0;
    try {
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        await api.post(`/shifts/${shift.id}/service-allocations`, {
          ...itemFields(it.psi), allocation_start_time: it.start, allocation_end_time: it.end,
          operational_ratio: form.operational_ratio || undefined, is_primary: i === 0 ? form.is_primary : false,
          notes: i === 0 ? (form.notes || undefined) : undefined,
          participant_id: shift.participant_id, ...(locked ? { reason: correctionReason.trim() } : {}),
        });
        saved += 1;
      }
    } catch (err) {
      if (saved > 0) {   // keep only what was not saved
        const rest = items.slice(saved);
        await reload();
        setForm(f => ({ ...f, ...(f._mode === 'catalogue' ? { line_item: rest[0].psi } : { participant_service_item_id: rest[0].psi }),
          allocation_start_time: rest[0].start, allocation_end_time: rest[0].end, is_primary: false, _extra: rest.slice(1) }));
      }
      err.itemLabel = `Line item ${saved + 1}`;
      throw err;
    }
    toast.success(`${items.length} service segments added`);
    closeEditor();
    await reload();
  };

  // Throws on a business-rule error so the sheet footer can stop at the first failure (owner clarification 2 Oct 2026).
  const performSave = async () => {
    if (!shift?.id) return;
    if (editing === 'new' && (form._extra || []).length) {
      await saveNewItems();
      return;
    }
    {
      const payload = {
        ...(form._mode === 'catalogue' ? lineItemFields(form.line_item, form.funding_plan_id) : { participant_service_item_id: form.participant_service_item_id }),
        allocation_start_time: form.allocation_start_time,
        allocation_end_time: form.allocation_end_time,
        operational_ratio: form.operational_ratio || undefined,
        is_primary: form.is_primary,
        notes: form.notes || undefined,
      };
      if (editing === 'new') {
        await api.post(`/shifts/${shift.id}/service-allocations`, { ...payload, participant_id: shift.participant_id, ...(locked ? { reason: correctionReason.trim() } : {}) });
        toast.success(locked ? 'Service segment added (recorded in shift history)' : 'Service segment added');
      } else if (locked) {
        await api.post(`/shifts/${shift.id}/service-allocations/${editing}/correct`, { ...payload, version: form._version, reason: correctionReason.trim() });
        toast.success('Segment corrected (audited)');
      } else {
        await api.put(`/shifts/${shift.id}/service-allocations/${editing}`, { ...payload, version: form._version });
        toast.success('Service segment updated');
      }
      closeEditor();
      await reload();
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try { await performSave(); }
    catch (err) { setInlineError(`${err?.itemLabel ? `${err.itemLabel}: ` : ''}${segmentErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  // Footer contract: one pending row while the inline editor is open.
  const editorOpen = !!editing;
  const editorLabel = editing === 'new' ? 'New segment' : `Segment ${form.allocation_start_time}–${form.allocation_end_time}`;
  useEffect(() => {
    if (!editorOpen) { draft.unregister(EDITOR_ID); return undefined; }
    draft.register({ id: EDITOR_ID, label: editorLabel, save: performSave, discard: closeEditor });
    return () => draft.unregister(EDITOR_ID);
  }, [editorOpen, editorLabel, form, correctionReason]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownError = inlineError || draft.inlineErrors[EDITOR_ID] || '';

  // Split: shorten the existing row to the split time, then add the remainder with the same service — two existing calls,
  // sequential, stop at the first business-rule error (inline).
  const performSplit = async () => {
    const { row, time } = splitting;
    const base = { ...(row.segment_kind === 'NO_AGREEMENT_SEGMENT' ? lineItemFields(row.branch_catalogue_item_id || row.service_catalog_id, row.funding_plan_id)
      : { participant_service_item_id: row.participant_service_item_id }), operational_ratio: row.operational_ratio || undefined, notes: row.notes || undefined };
    await api.put(`/shifts/${shift.id}/service-allocations/${row.id}`, { ...base, allocation_start_time: row.allocation_start_time, allocation_end_time: time, is_primary: !!row.is_primary, version: row.version });
    await api.post(`/shifts/${shift.id}/service-allocations`, { ...base, participant_id: shift.participant_id, allocation_start_time: time, allocation_end_time: row.allocation_end_time, is_primary: false });
    toast.success('Segment split');
    setSplitting(null);
    await reload();
  };
  const handleSplit = async () => {
    setSaving(true);
    try { await performSplit(); }
    catch (err) { setInlineError(segmentErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleArchive = async (row) => {
    if (!shift?.id) return;
    if (!window.confirm(`Remove the ${row.allocation_start_time}–${row.allocation_end_time} segment? The change is audited.`)) return;
    try {
      await api.delete(`/shifts/${shift.id}/service-allocations/${row.id}`, { data: { reason: 'removed on scheduling board' } });
      toast.success('Service segment removed');
      await reload();
    } catch (err) {
      toast.error(segmentErrorMessage(err));
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (!canView) return null;

  return (
    <Card data-testid="service-activity-breakdown-panel">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Layers className="w-4 h-4" /> Participant Service Segments
          {locked && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700" data-testid="segment-locked-badge">
              <Lock className="w-3 h-3 mr-1" /> Locked
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2 text-sm">
        {!compact && <div className="rounded-md border bg-blue-50 border-blue-200 px-2.5 py-2 text-[11px] leading-relaxed text-blue-900 flex gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            Each segment is a timed part of this participant's shift linked to an agreement line and service item.
            <strong className="block mt-0.5">Only services authorised by the ACTIVE agreement can be selected; the signed rate is never changed here.</strong>
          </span>
        </div>}

        {/* Enterprise Phase 1.5 — Operational Activity Tile (admin-only, umbrella-gated) */}
        {!compact && canViewOperationalTile && opsSummary?.feature_enabled && opsSummary?.summary && (
          <div
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 space-y-1.5"
            data-testid="operational-activity-tile"
          >
            <div className="flex items-center justify-between text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-medium">
                <Activity className="w-3.5 h-3.5" /> Operational Activity (admin)
              </span>
              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                Read-only · Phase 1
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-activity-count">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Activities</div>
                <div className="tabular-nums font-semibold text-slate-800">{opsSummary.summary.activity_count ?? 0}</div>
              </div>
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-unique-services">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Services</div>
                <div className="tabular-nums font-semibold text-slate-800">{opsSummary.summary.unique_service_count ?? 0}</div>
              </div>
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-total-hours">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Total hours</div>
                <div className="tabular-nums font-semibold text-slate-800">{fmtHours(opsSummary.summary.total_activity_hours)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {opsSummary.summary.primary_activity_id && (
                <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700" data-testid="ops-tile-primary-tag">
                  <Star className="w-3 h-3 mr-1" /> Primary set
                </Badge>
              )}
              {opsSummary.summary.has_transport_marker && (
                <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid="ops-tile-transport-tag">
                  <Car className="w-3 h-3 mr-1" /> Transport marker
                </Badge>
              )}
              {!opsSummary.summary.primary_activity_id && !opsSummary.summary.has_transport_marker && (
                <span className="text-[10px] text-slate-500" data-testid="ops-tile-no-tags">No operational tags</span>
              )}
            </div>
          </div>
        )}

        {/* Enterprise Phase 2A — Financial Preview Tile (admin-only, flag-gated, READ-ONLY) */}
        {!compact && canViewPreviewTile && preview?.feature_enabled && preview?.summary && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50/40 px-2.5 py-2 space-y-1.5"
            data-testid="preview-tile"
          >
            <div className="flex items-center justify-between text-[11px] text-amber-900">
              <span className="flex items-center gap-1.5 font-medium">
                <DollarSign className="w-3.5 h-3.5" /> Financial Preview (admin · projection)
              </span>
              <Badge
                variant="outline"
                className={
                  preview.summary.severity === 'CRITICAL'
                    ? 'text-[10px] border-red-400 text-red-700'
                    : preview.summary.severity === 'WARN'
                    ? 'text-[10px] border-amber-400 text-amber-700'
                    : 'text-[10px] border-emerald-300 text-emerald-700'
                }
                data-testid="preview-tile-severity"
              >
                {preview.summary.severity === 'CRITICAL' && <AlertTriangle className="w-3 h-3 mr-1" />}
                {preview.summary.severity}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-lines">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Preview lines</div>
                <div className="tabular-nums font-semibold text-slate-800">{preview.summary.allocation_count}</div>
              </div>
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-hours">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected hours</div>
                <div className="tabular-nums font-semibold text-slate-800">{fmtHours(preview.summary.total_hours)}</div>
              </div>
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-amount">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected amount</div>
                <div className="tabular-nums font-semibold text-slate-800">
                  ${Number(preview.summary.total_amount || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-amber-900/80 italic">
              Read-only projection. Invoices, claims, payroll, and funding remain unchanged.
            </div>
            {preview.summary.severity !== 'INFO' && (
              <div
                className="rounded border border-amber-300 bg-white/60 px-2 py-1 text-[10px] text-amber-900"
                data-testid="preview-tile-drift-banner"
              >
                Reconciliation drift detected (
                {preview.reconciliation?.hours_delta != null
                  ? `${preview.reconciliation.hours_delta}h`
                  : '—'}
                ). This preview will NOT be promoted to live billing until the source
                allocations are corrected.
              </div>
            )}
            {preview.summary.severity === 'INFO' &&
              preview.reconciliation?.lifecycle_state === 'pending_approval' &&
              preview.reconciliation?.raw_severity &&
              preview.reconciliation.raw_severity !== 'INFO' && (
                <div
                  className="rounded border border-slate-200 bg-white/70 px-2 py-1 text-[10px] text-slate-700"
                  data-testid="preview-tile-pending-banner"
                >
                  Projection only — drift comparison will activate once the
                  timesheet is approved. Allocations are balanced; nothing to fix here.
                </div>
              )}

            {/* Enterprise Phase 2A.5 — Preview Lines Drawer (admin · expandable · READ-ONLY) */}
            <button
              type="button"
              onClick={() => setPreviewLinesOpen((v) => !v)}
              className="w-full flex items-center justify-between text-[10px] text-slate-700 hover:text-slate-900 px-1 py-0.5 rounded hover:bg-amber-100/60 transition-colors"
              data-testid="preview-lines-drawer-toggle"
              aria-expanded={previewLinesOpen}
            >
              <span className="flex items-center gap-1 font-medium">
                {previewLinesOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Preview lines ({(preview.lines || []).length})
              </span>
              <span className="italic text-slate-500">Projection · no live writes</span>
            </button>
            {previewLinesOpen && (
              <div className="space-y-1.5 pt-1" data-testid="preview-lines-drawer">
                {/* Phase 2A.6 — Legacy Invoice Comparison Bar (admin · read-only) */}
                {preview.reconciliation && (
                  (() => {
                    const legacy = Number(preview.reconciliation.legacy_invoice_amount || 0);
                    const projected = Number(preview.reconciliation.projected_amount || 0);
                    const delta = Number(preview.reconciliation.amount_delta || 0);
                    const sev = preview.reconciliation.amount_severity || preview.summary.severity;
                    const status = sev === 'INFO' ? 'MATCH'
                      : sev === 'WARN' ? 'MINOR_VARIANCE'
                      : 'MATERIAL_DIFFERENCE';
                    const statusClass = status === 'MATCH'
                      ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                      : status === 'MINOR_VARIANCE'
                      ? 'border-amber-300 text-amber-700 bg-amber-50'
                      : 'border-red-300 text-red-700 bg-red-50';
                    const pct = legacy > 0 ? ((delta / legacy) * 100).toFixed(2) : null;
                    return (
                      <div
                        className="rounded border border-slate-200 bg-white px-2.5 py-2 grid grid-cols-4 gap-2 items-center"
                        data-testid="comparison-bar"
                      >
                        <div data-testid="comparison-bar-legacy">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Legacy invoice</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${legacy.toFixed(2)}
                          </div>
                        </div>
                        <div data-testid="comparison-bar-projected">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected preview</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${projected.toFixed(2)}
                          </div>
                        </div>
                        <div data-testid="comparison-bar-delta">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Difference</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${delta.toFixed(2)}
                            {pct != null && (
                              <span className="text-[9px] text-slate-500 ml-1">({pct}%)</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant="outline"
                            className={`text-[9px] ${statusClass}`}
                            data-testid="comparison-bar-status"
                          >
                            {status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })()
                )}

                {(preview.lines || []).length === 0 ? (
                  <div className="text-[10px] text-slate-500 italic px-1 py-2" data-testid="preview-lines-empty">
                    No preview lines available.
                  </div>
                ) : (
                  (preview.lines || []).map((ln) => (
                    <div
                      key={ln.allocation_id}
                      className="rounded border border-amber-200 bg-white px-2 py-1.5 text-[10px] space-y-0.5"
                      data-testid={`preview-line-row-${ln.allocation_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 font-medium tabular-nums text-slate-800">
                          #{ln.allocation_order ?? '—'} ·{' '}
                          {ln.allocation_start_time || '—'}–{ln.allocation_end_time || '—'}
                          {ln.is_primary && (
                            <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">
                              <Star className="w-2.5 h-2.5 mr-0.5" /> Primary
                            </Badge>
                          )}
                          {ln.transport_segment && (
                            <Badge variant="outline" className="text-[9px] border-indigo-300 text-indigo-700">
                              <Car className="w-2.5 h-2.5 mr-0.5" /> Transport
                            </Badge>
                          )}
                        </div>
                        <span className="tabular-nums font-semibold text-slate-800" data-testid={`preview-line-amount-${ln.allocation_id}`}>
                          ${Number(ln.amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[9px] text-slate-600">
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Support item</span>
                          <div className="text-slate-700 truncate" title={ln.support_item_number || '—'}>
                            {ln.support_item_number || '—'}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Hours × Rate</span>
                          <div className="tabular-nums text-slate-700">
                            {fmtHours(ln.duration_hours)} × ${Number(ln.rate || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Funding plan</span>
                          <div className="text-slate-700 truncate" title={fundingPlanLabel(fundingPlanLookup.get(ln.funding_plan_id)) || ln.funding_plan_id || '—'}>
                            {ln.funding_plan_id
                              ? fundingPlanLabel(fundingPlanLookup.get(ln.funding_plan_id) || { id: ln.funding_plan_id })
                              : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {(preview.issues || []).length > 0 && (
                  <div
                    className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] text-amber-900 space-y-0.5"
                    data-testid="preview-lines-issues"
                  >
                    <div className="font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Preview issues
                    </div>
                    {preview.issues.map((iss, idx) => (
                      <div key={idx} className="pl-4 text-[9px]" data-testid={`preview-issue-${idx}`}>
                        <span className="font-mono">{iss.code}</span>
                        <span className="text-slate-500"> · {iss.severity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {locked && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900 flex gap-2" data-testid="segment-locked-banner">
            <Lock className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>
              {lockMessage(locked)}{' '}
              {!canCorrect
                ? 'Only a rostering manager can add or correct its service segments now.'
                : 'You can still add or correct service segments. Give a short reason; it is recorded in the shift history.'}
              {BILLING_FINAL_LOCKS.has(locked) ? ' A change here does not alter what was already billed; the billing record is corrected separately.' : ''}
            </span>
          </div>
        )}
        {!locked && noAgreementSegments && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900" data-testid="segment-no-agreement-banner">
            {shift?.billing_source === 'FUNDING_PLAN' ? 'This shift was rostered without a service agreement.' : `No usable service agreement line for this participant on ${shift?.date}.`}
            {' '}Segments use a {useBranchCatalogue ? 'branch catalogue' : 'catalogue'} line item and are billed from the participant's funding plan.
          </div>
        )}
        {!locked && !noAgreementSegments && authorised && !authorised.agreement && (
          <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-900" data-testid="segment-no-authority-banner">
            No ACTIVE service agreement authorises services for this participant on {shift?.date}
            {authorised.agreement_status ? <> (latest agreement is <span className="font-mono">{authorised.agreement_status}</span>)</> : null}.
            Segments cannot be added until an agreement is ACTIVE.
          </div>
        )}

        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : myRows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2" data-testid="segments-empty">
            No service segments yet. {(canEdit || canAddWithReason) && canCreate ? 'Add a segment to roster a service for part or all of this shift.' : ''}
          </div>
        ) : (
          <div className="space-y-1.5">
            {[...myRows].sort((a, b) => (a.allocation_order || 0) - (b.allocation_order || 0)).map(row => (
              <div
                key={row.id}
                className="rounded-md border bg-slate-50 p-2 flex items-start justify-between gap-2 text-xs"
                data-testid={`segment-row-${row.id}`}
              >
                <div className="flex-1 space-y-0.5">
                  <div className="flex items-center gap-1.5 font-medium tabular-nums flex-wrap">
                    {row.allocation_start_time}–{row.allocation_end_time}
                    {row.scheduled_end_at && row.scheduled_end_at.slice(0, 10) !== String(shift?.date) && (
                      <span className="text-[10px] text-muted-foreground font-normal">(ends {row.scheduled_end_at.slice(0, 10)})</span>
                    )}
                    <span className="text-muted-foreground font-normal">· {fmtHours(row.duration_hours)}</span>
                    {row.operational_ratio && (
                      <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-700" title="Operational delivery ratio — never changes the signed rate">{row.operational_ratio}</Badge>
                    )}
                    {row.is_primary && (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">Primary</Badge>
                    )}
                    {row.segment_kind === 'LEGACY_ALLOCATION' && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Legacy allocation</Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    Service: <span className="text-foreground">{serviceLabel(row)}</span>
                    {row.agreement_line_id && (() => { const ln = lineLookup.get(row.agreement_line_id); const ref = ln ? `${authorised?.agreement?.agreement_no || 'Agreement'} · ${ln.support_item_number || ln.service_name}` : 'line not on the current ACTIVE agreement'; return <span className="text-[10px]" title={row.agreement_line_id} data-testid={`segment-line-ref-${row.id}`}> · {ref}</span>; })()}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Delivery: <span className="font-mono" data-testid={`segment-outcome-${row.id}`}>{row.delivery_outcome || 'not yet recorded'}</span>{row.actual_start && <> ({String(row.actual_start).slice(11, 16)}–{String(row.actual_end).slice(11, 16)})</>}
                    {row.funding_plan_id && <> · {fundingPlanLabel(fundingPlanLookup.get(row.funding_plan_id) || { id: row.funding_plan_id })}</>}
                  </div>
                  {row.notes && <div className="text-muted-foreground italic">"{row.notes}"</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {canEdit && row.segment_kind !== 'LEGACY_ALLOCATION' && (
                    <>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEdit(row)} disabled={!canUpdate} title={canUpdate ? 'Edit segment' : permissionReason('service_allocations.update')} data-testid={`segment-edit-${row.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setInlineError(''); setSplitting({ row, time: '' }); }} disabled={!canUpdate || !canCreate} title={canUpdate && canCreate ? 'Split segment at a time' : permissionReason(canUpdate ? 'service_allocations.create' : 'service_allocations.update')} data-testid={`segment-split-${row.id}`}>
                        <Scissors className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {canAddWithReason && row.segment_kind !== 'LEGACY_ALLOCATION' && (
                    <Button size="sm" variant="ghost" className="h-6 px-1 text-[10px] text-amber-800" title="Correct this segment (reason required, kept in shift history)" onClick={() => { setCorrectionReason(''); openEdit(row); }} data-testid={`segment-correct-${row.id}`}>
                      Correct
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleArchive(row)} disabled={!canDelete} title={canDelete ? 'Remove segment' : permissionReason('service_allocations.delete')} data-testid={`segment-delete-${row.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Segment summary */}
        {myRows.length > 0 && (
          <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t pt-1.5">
            <span>Shift: <span className="tabular-nums font-medium">{fmtHours(shiftDuration)}</span></span>
            <span>Segments: <span className={`tabular-nums font-medium ${overAllocated ? 'text-red-600' : ''}`}>{fmtHours(allocated)}</span></span>
            <span>Unallocated: <span className="tabular-nums font-medium">{fmtHours(remaining)}</span></span>
          </div>
        )}

        {/* Action buttons */}
        {splitting && (
          <div className="rounded-md border bg-background p-2.5 space-y-2 text-xs" data-testid="segment-split-editor">
            <div className="font-medium">Split {splitting.row.allocation_start_time}–{splitting.row.allocation_end_time} at</div>
            <TrinitiTimePicker value={splitting.time} onChange={(v) => setSplitting((x) => ({ ...x, time: v }))} testId="segment-split-time" />
            {shownError && <div className="text-red-700" data-testid="segment-inline-error">{shownError}</div>}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSplitting(null); setInlineError(''); }}>Cancel</Button>
              <Button type="button" size="sm" className="h-7 text-xs" disabled={saving || !splitting.time} onClick={handleSplit} data-testid="segment-split-save">{saving ? 'Saving…' : 'Split'}</Button>
            </div>
          </div>
        )}
        {(canEdit || canAddWithReason) && !editing && (
          <div className="pt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs w-full"
              onClick={openNew}
              disabled={!canCreate}
              title={canCreate ? undefined : permissionReason('service_allocations.create')}
              data-testid="add-segment-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add segment
            </Button>
          </div>
        )}

        {/* Inline editor */}
        {editing && (
          <form onSubmit={handleSave} className="rounded-md border bg-background p-2.5 space-y-2 text-xs" data-testid="segment-editor">
            <div className="flex items-center justify-between">
              <div className="font-medium">{editing === 'new' ? 'New segment' : 'Edit segment'}</div>
              <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={closeEditor}>
                <X className="w-3 h-3" />
              </Button>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Start</Label>
                <TrinitiTimePicker
                  value={form.allocation_start_time}
                  onChange={(v) => setForm(f => ({ ...f, allocation_start_time: v }))}
                  testId="segment-start-input"
                />
              </div>
              <div>
                <Label className="text-[10px]">End</Label>
                <TrinitiTimePicker
                  value={form.allocation_end_time}
                  onChange={(v) => setForm(f => ({ ...f, allocation_end_time: v }))}
                  testId="segment-end-input"
                />
              </div>
            </div>
            {form._mode === 'catalogue' ? (
            <div className="space-y-2" data-testid="segment-catalogue-block">
              <div>
                <Label className="text-[10px]">Line item ({useBranchCatalogue ? 'branch catalogue' : 'service catalogue'} — no agreement line, billed from the funding plan)</Label>
                <LineItemSelect options={catalogueOptions.map(o => ({ value: o.id, label: o.label, keywords: o.code || o.support_item_number || '' }))}
                  value={form.line_item} onChange={(v) => setForm(f => ({ ...f, line_item: v }))} placeholder="Select the line item"
                  emptyText={catalogueOptions.length ? undefined : `No line items available for this branch on ${shift?.date}.`}
                  className="h-7 text-xs" testId="segment-line-item-select" />
              </div>
              <div>
                <Label className="text-[10px]">Funding plan</Label>
                <Select value={form.funding_plan_id || 'auto'} onValueChange={(v) => setForm(f => ({ ...f, funding_plan_id: v === 'auto' ? '' : v }))}>
                  <SelectTrigger className="h-7 text-xs" data-testid="segment-funding-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Plan covering {shift?.date} (automatic)</SelectItem>
                    {(fundingPlans || []).map(p => <SelectItem key={p.id} value={p.id}>{fundingPlanLabel(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {usableLines && (
                <button type="button" className="text-[10px] text-primary hover:underline" data-testid="segment-use-agreement-line"
                  onClick={() => setForm(f => ({ ...f, _mode: 'psi', participant_service_item_id: psiOptions.find(o => !o.disabled)?.value || '' }))}>
                  ← Use the agreement line instead
                </button>
              )}
            </div>
            ) : (
            <div>
              <Label className="text-[10px]">Authorised service (ACTIVE agreement {authorised?.agreement?.agreement_no || ''})</Label>
              <LineItemSelect options={psiOptions} value={form.participant_service_item_id}
                onChange={(v) => setForm(f => ({ ...f, participant_service_item_id: v }))} placeholder="Select an authorised service"
                emptyText={psiOptions.length ? undefined : 'No participant service items on the ACTIVE agreement. Schedule one from the agreement line first.'}
                className="h-7 text-xs" testId="segment-service-select" />
              {noAgreementSegments && (
                <button type="button" className="text-[10px] text-primary hover:underline mt-1" data-testid="segment-use-catalogue"
                  onClick={() => setForm(f => ({ ...f, _mode: 'catalogue', participant_service_item_id: '', line_item: shift?.branch_catalogue_item_id || shift?.service_catalog_id || '' }))}>
                  Use a catalogue line item instead (billed from the funding plan)
                </button>
              )}
            </div>
            )}
            {editing === 'new' && (
              <div className="space-y-1.5" data-testid="segment-extra-items">
                {(form._extra || []).map((x, i) => (
                  <div key={x._key} className="grid grid-cols-[1fr_auto_auto_auto] gap-1.5 items-center" data-testid={`segment-extra-${i}`}>
                    <LineItemSelect options={extraItemOptions} value={x.psi} onChange={(v) => updateExtraItem(x._key, { psi: v })}
                      placeholder={form._mode === 'catalogue' ? 'Select the line item' : 'Select an authorised service'}
                      className="h-7 text-xs" testId={`segment-extra-item-${i}`} />
                    <TrinitiTimePicker value={x.start} onChange={(v) => updateExtraItem(x._key, { start: v })} testId={`segment-extra-start-${i}`} />
                    <TrinitiTimePicker value={x.end} onChange={(v) => updateExtraItem(x._key, { end: v })} testId={`segment-extra-end-${i}`} />
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeExtraItem(x._key)} aria-label="Remove line item" data-testid={`segment-extra-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <button type="button" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1" onClick={addExtraItem}
                  disabled={!mainItemValue || !form.allocation_start_time || !form.allocation_end_time} data-testid="segment-add-line-item">
                  <Plus className="h-3 w-3" /> Add another line item
                </button>
              </div>
            )}
            <div>
              <Label className="text-[10px]">Operational ratio (delivery attribute — never alters the signed rate)</Label>
              <Select value={form.operational_ratio} onValueChange={(v) => setForm(f => ({ ...f, operational_ratio: v }))}>
                <SelectTrigger className="h-7 text-xs" data-testid="segment-ratio-select"><SelectValue placeholder="Ratio" /></SelectTrigger>
                <SelectContent>
                  {RATIOS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="segment-primary"
                checked={form.is_primary}
                onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                className="h-3.5 w-3.5 rounded accent-primary"
                data-testid="segment-primary-checkbox"
              />
              <Label htmlFor="segment-primary" className="text-[10px] cursor-pointer">Primary segment of this shift</Label>
            </div>
            {locked && (
              <div>
                <Label className="text-[10px] text-amber-800">Reason (required: {lockMessage(locked)})</Label>
                <Input value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} className="h-7 text-xs" placeholder={editing === 'new' ? 'e.g. Service was not planned before the worker clocked in' : 'Why is this change needed after delivery?'} data-testid="segment-correction-reason" />
              </div>
            )}
            <div>
              <Label className="text-[10px]">Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="h-7 text-xs"
                placeholder="e.g. handover 10:30"
                data-testid="segment-notes-input"
              />
            </div>
            {shownError && <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-800" data-testid="segment-inline-error">{shownError}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={closeEditor}>Cancel</Button>
              <Button type="submit" size="sm" className="h-7 text-xs" disabled={saving || (form._mode === 'catalogue' ? !form.line_item : !form.participant_service_item_id) || (locked && correctionReason.trim().length < 3)} data-testid="segment-save-btn">
                {saving ? 'Saving…' : (editing === 'new' ? ((form._extra || []).length ? `Add ${(form._extra || []).length + 1} segments` : 'Add segment') : (locked ? 'Save correction' : 'Save changes'))}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceActivityBreakdownPanel;
